import { useTranslation } from 'react-i18next';
import {
  CURRENCY,
  ENTITY_CODE_INSURANCE,
  ENTITY_CODE_TAKAFUL,
  I18N_LANGUAGE_EN,
  INSURANCE,
  ProductLabel,
} from '../constants';
import { Gender, PaymentMode } from '../enums';
import { useAppSelector } from '../reducers';
import { ApplicationFormValues } from '../types/form/application';
import { QuestionnaireFormValues } from '../types/form/questionnaire';
import { useOptions } from './useOptions';
import { useProductType, useRoute } from './useRoute';
import { QuotationFormValues } from '../types/form';
import { useCallback } from 'react';

// types.ts
export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_variant: string;
  currency: string;
  price: number;
  quantity: number;
  coupon: string;
  item_billing_type: string;
  deductible_status: string;
  epolicy_number?: string; // Only for purchase
};

export type EcommerceData = {
  currency: string;
  value: number;
  coupon: string;
  items: EcommerceItem[];
  epolicy_number?: string; // Only for purchase
  transaction_id?: string; // Only for purchase
};

export type CommonGAType = {
  event: string;
  event_category: string;
  event_action: string;
  event_label: string;
};

export type GAEvent =
  | 'add_to_cart'
  | 'purchase'
  | 'begin_checkout'
  | 'add_payment_info'
  | 'health_submit_health_declaration'
  | 'select_language'
  | 'health_begin_quotation'
  | 'go_to_page';

export interface GAFactoryParams {
  transactionId?: string;
  epolicyNumber?: string;
  value?: number;
  item_id?: string;
  billing_type?: string;
  item_variant?: string;
  deductible_status?: number;
  price?: number;
  // add more optional dynamic params if needed
}

export interface GaFactoryTypes {
  planCode?: string;
  paymentModeFormValue?: string;
  deductible?: number;
  quotaionsValue?: QuotationFormValues;
  QuestionnaireValues?: QuestionnaireFormValues;
  applicationValues?: ApplicationFormValues;
  purchaseVaues?: GAFactoryParams;
}

const eventAndCategoryMapping = {
  select_language: {
    event_action: 'Select Language',
    event_category_postfix: '',
    event_label: '',
  },
  health_begin_quotation: {
    event_action: 'Begin Quotation',
    event_category_postfix: '',
    event_label: 'Quotation',
  },
  go_to_page: {
    event_action: 'Go To Page',
    event_category_postfix: '',
    event_label: 'Analysis And Financial Budget Calculator',
  },
  add_to_cart: {
    event_action: 'Add To Cart',
    event_category_postfix: '- Choose Plan',
    event_label: '',
  },
  health_submit_health_declaration: {
    event_action: 'Submit Health Declaration',
    event_category_postfix: '- Health Declaration',
    event_label: 'Health Declaration',
  },
  begin_checkout: {
    event_action: 'Begin Checkout',
    event_category_postfix: '- Fill In The Details',
    event_label: '',
  },
  add_payment_info: {
    event_action: 'Add Payment Info',
    event_category_postfix: '- Declaration And Payment',
    event_label: '',
  },
  purchase: {
    event_action: 'Purchase',
    event_category_postfix: '- Payment Success',
    event_label: '',
  },
};

export const useGAFactory = () => {
  const productType = useProductType();
  const productLabel = ProductLabel[productType];
  const routeSection = useRoute();
  const { getProtections } = useOptions();
  const application = useAppSelector((state) => state.application);
  const options = useOptions();
  const { i18n } = useTranslation();
  const GAFactory = useCallback(
    (eventName: GAEvent, GaFactoryParms: GaFactoryTypes) => {
      const {
        planCode,
        paymentModeFormValue,
        deductible,
        QuestionnaireValues,
        applicationValues,
        quotaionsValue,
        purchaseVaues,
      } = GaFactoryParms;
      const plans = application.plans;
      const choosedPlan =
        plans.filter((item) => item.planCode === planCode) || 12;
      const protectionList = getProtections(
        application.formValues?.quotation?.life_insured.gender as Gender,
      );
      const paymentModeValue = Object.keys(PaymentMode).filter(
        (item) =>
          PaymentMode[item as keyof typeof PaymentMode] ===
            paymentModeFormValue || '',
      );
      const insecuredPerson = protectionList.filter(
        (item) =>
          application.formValues.quotation?.life_insured?.relationship ===
          item.value,
      );
      const policyOwnerOccupation = options.occupations
        .flatMap((item) =>
          item.options.map((opt) => ({
            industry: item.industry,
            ...opt,
          })),
        )
        .find((o) => o.value === quotaionsValue?.policy_owner?.occupation);
      const lifeScecuredOwnerOccupations = options.occupations
        .flatMap((item) =>
          item.options.map((opt) => ({
            industry: item.industry,
            ...opt,
          })),
        )
        .find((o) => o.value === quotaionsValue?.life_insured.occupation);

      const relationshipKey =
        quotaionsValue?.life_insured?.relationship ||
        application.formValues.quotation?.life_insured.relationship;
      const relationshipData =
        protectionList.find((item) => item.value === relationshipKey)?.label ??
        'N/A';

      const item: EcommerceItem[] = [
        {
          item_id: planCode || purchaseVaues?.item_id || '',
          item_name: `${productLabel} ${routeSection['entity']}`,
          item_brand:
            routeSection['entity'] === INSURANCE
              ? ENTITY_CODE_INSURANCE
              : ENTITY_CODE_TAKAFUL,
          item_category: 'Health',
          item_category2: `Medical Card ${routeSection['entity']} Plan`,
          item_category3: application.formValues?.quotation
            ? application.formValues.quotation.policy_owner
              ? 'Family'
              : 'Individual'
            : '',
          item_category4: insecuredPerson[0]?.label,
          item_category5: 'One Plan',
          item_variant: planCode || '',
          currency: CURRENCY,
          price: choosedPlan[0]
            ? Number(choosedPlan[0]?.yearlyPremium.toFixed(2))
            : purchaseVaues?.price
              ? purchaseVaues?.price
              : 0,
          quantity: 1,
          coupon: 'N/A',
          item_billing_type: paymentModeValue[0],
          deductible_status:
            (deductible || 0) > 0 ? 'deductible' : 'Non-deductible',
        },
      ];
      const config = eventAndCategoryMapping[eventName];
      const eventCategoryName =
        productLabel === 'i-Medical Card'
          ? 'i-MedicalCard Elite'
          : productLabel;
      const routeSectionEntity =
        routeSection['entity'].charAt(0).toUpperCase() +
        routeSection['entity'].slice(1);
      const commonFields: CommonGAType = {
        event: eventName,
        event_category: `${eventCategoryName}-${routeSectionEntity} ${config.event_category_postfix}`,
        event_action: config.event_action,
        event_label: config.event_label
          ? config.event_label
          : `${productLabel} ${routeSection['entity']}`,
      };
      const insuredSource =
        relationshipData === 'Self'
          ? applicationValues?.policy_owner
          : applicationValues?.life_insured;
      const insecuredPersonBeginChekout = {
        insured_person_birth_date: insuredSource?.personalDetail?.dob,

        insured_person_gender:
          insuredSource?.personalDetail?.gender === 'F'
            ? 'Female'
            : insuredSource?.personalDetail?.gender === 'M'
              ? 'Male'
              : 'N/A',

        insured_person_nationality:
          options.nationalities.find(
            (item) => item.value === insuredSource?.personalDetail?.nationality,
          )?.label || 'N/A',

        insured_person_marital_status:
          options.maritalStatuses.find(
            (item) =>
              item.value === insuredSource?.personalDetail?.maritalStatus,
          )?.label || 'N/A',

        insured_person_race:
          options.races.find(
            (item) => item.value === insuredSource?.personalDetail?.race,
          )?.label || 'N/A',

        insured_person_occupation:
          options.occupations
            ?.flatMap(
              (i) =>
                i.options?.map((o) => ({ industry: i.industry, ...o })) ?? [],
            )
            .find((o) => o.value === insuredSource?.personalDetail?.occupation)
            ?.label ?? 'N/A',

        insured_person_employer_name:
          insuredSource?.personalDetail?.employer || 'N/A',

        insured_person_monthly_income:
          insuredSource?.personalDetail?.income || 'N/A',
      };

      switch (eventName) {
        case 'select_language':
          return {
            ...commonFields,
            event_label:
              i18n.language === I18N_LANGUAGE_EN ? 'English' : 'Bahasa Melayu',
          };
        case 'go_to_page':
          return { ...commonFields };
        case 'health_begin_quotation':
          return {
            ...commonFields,
            gender:
              quotaionsValue?.life_insured.gender === 'F' ? 'Female' : 'Male',
            occupation:
              relationshipData !== 'Self'
                ? policyOwnerOccupation?.label
                : lifeScecuredOwnerOccupations?.label,
            insured_person: relationshipData,
            insured_person_occupation:
              relationshipData === 'Self'
                ? lifeScecuredOwnerOccupations?.label
                : relationshipData === 'Son' || relationshipData === 'Daughter'
                  ? 'Pre-school/primary/secondary school student'
                  : relationshipData === 'Spouse'
                    ? lifeScecuredOwnerOccupations?.label
                    : 'N/A',
            policy_type: 'New Policy',
            protection_type: 'Medical',
          };
        case 'add_to_cart':
        case 'add_payment_info':
          return {
            ...commonFields,
            policy_type: 'New Policy',
            protection_type: 'Medical',
            ecommerce: {
              currency: CURRENCY,
              value: Number(choosedPlan[0]?.yearlyPremium.toFixed(2)),
              coupon: 'N/A',
              items: item,
            } as EcommerceData,
          };
        case 'purchase':
          return {
            ...commonFields,
            policy_type: 'New Policy',
            protection_type: 'Medical',
            ecommerce: {
              currency: CURRENCY,
              value:
                Number(choosedPlan[0]?.yearlyPremium.toFixed(2)) ||
                purchaseVaues?.price,
              coupon: 'N/A',
              items: [
                { ...item[0], epolicy_number: purchaseVaues?.epolicyNumber },
              ],
              transaction_id: purchaseVaues?.transactionId,
              epolicy_number: purchaseVaues?.epolicyNumber,
            } as EcommerceData,
          };
        case 'health_submit_health_declaration':
          return {
            ...commonFields,
            height: QuestionnaireValues?.height,
            weight: QuestionnaireValues?.weight,
            question_1:
              QuestionnaireValues?.declarations[0].answer === 'N'
                ? 'No'
                : 'Yes',
            question_2:
              QuestionnaireValues?.declarations[1].answer === 'N'
                ? 'No'
                : 'Yes',
            question_3:
              QuestionnaireValues?.declarations[2].answer === 'N'
                ? 'No'
                : 'Yes',
            policy_type: 'New Policy',
            protection_type: 'Medical',
          };
        case 'begin_checkout':
          return {
            ...commonFields,
            policy_type: 'New Policy',
            protection_type: 'Medical',
            transaction_purpose: options.purposeOfTransactions.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail
                  .purposeOfTransaction,
            )?.label,
            birth_date: applicationValues?.policy_owner?.personalDetail?.dob,
            gender:
              applicationValues?.policy_owner?.personalDetail?.gender === 'F'
                ? 'Female'
                : 'Male',
            nationality: options.nationalities.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.nationality,
            )?.label,
            marital_status: options.maritalStatuses.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.maritalStatus,
            )?.label,
            applicant_race: options.races.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.race,
            )?.label,
            occupation: options.occupations[0].options.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.occupation,
            )?.label,
            employer_name:
              applicationValues?.policy_owner.personalDetail.employer,
            monthly_income:
              applicationValues?.policy_owner.personalDetail.income,
            source_of_wealth: options.wealthSource.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.wealthSource,
            )?.label,
            source_of_fund: options.fundSource.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.fundSource,
            )?.label,
            postcode:
              applicationValues?.policy_owner.personalDetail.homeAddress
                .postcode,
            city: applicationValues?.policy_owner.personalDetail.homeAddress
              .city,
            state: options.states.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.homeAddress
                  .state,
            )?.label,
            country: options.countries.find(
              (item) =>
                item.value ===
                applicationValues?.policy_owner.personalDetail.homeAddress
                  .country,
            )?.label,
            bank_name: options.banks.find(
              (item) => item.value === applicationValues?.bankDetail.bankName,
            )?.label,
            ecommerce: {
              currency: CURRENCY,
              value: choosedPlan[0]?.yearlyPremium,
              coupon: 'N/A',
              items: item,
            } as EcommerceData,
            ...insecuredPersonBeginChekout,
          };
      }
    },
    [
      application,
      options,
      productLabel,
      routeSection,
      i18n.language,
      getProtections,
    ],
  );

  return GAFactory;
};
